import type { PlainObject } from 'simplytyped';
import { assertKeyExists } from '../assertions';
import isObject from 'lodash/isObject';
import type { InputWrapperProps, TTableCellContent } from '../../components';
import { isArray, uniq } from 'lodash';

export const indexItems = <T extends PlainObject>(
  items: ReadonlyArray<T> = [],
  idKey = 'id'
): Record<string, T> => {
  // @TODO, can this be replaced by a type alias? E.g. Id extends string etc...
  items.forEach((item) => assertKeyExists(item, idKey));

  return items.reduce(
    (idx, item) => ({
      ...idx,
      [item[idKey]]: item
    }),
    {}
  );
};

export const termToDurationString = (
  {
    min,
    max: _max = -1,
    unit = 'month',
    pluralUnit: _pluralUnit
  }: {
    min: number;
    max: number;
    unit?: string;
    pluralUnit?: string;
  },
  { hideUnit = false } = {}
) => {
  // .toFixed is required as 1.99 % 1 = 0.9900000000000002
  const max = (_max % 1).toFixed(2) === '0.99' ? Math.round(_max) : _max;
  const pluralUnit = _pluralUnit ?? `${unit}s`;
  const maxStr = max !== -1 ? ` - ${max}` : ``;
  const unitStr = !hideUnit
    ? max !== -1
      ? ` ${max > 1 ? pluralUnit : unit}`
      : ` ${max > 1 ? pluralUnit : unit} +`
    : '';

  return `${min}${maxStr}${unitStr}`;
};

export const errorToStackString = (error: unknown): string => {
  if (isObject(error)) {
    if ('stack' in error) {
      return String(error.stack);
    }

    if ('message' in error) {
      return String(error.message);
    }

    return JSON.stringify(error);
  }

  // if error is primitive then just string cast it
  return String(error);
};

const traverseObject = (
  obj: unknown,
  callback: (obj: Record<string, unknown>) => void
) => {
  if (isObject(obj)) {
    callback(obj as Record<string, unknown>);
  }

  if (isArray(obj) || (isObject(obj) && !(obj instanceof HTMLElement))) {
    Object.values(obj)
      .filter((v) => v)
      .forEach((value) => {
        traverseObject(value, callback);
      });
  }
};

export const errorToMessageString = (error: unknown): string => {
  if (isObject(error) || isArray(error)) {
    const messages: Array<string> = [];
    traverseObject(error, (obj) => {
      if ('message' in (obj || {})) {
        messages.push(String(obj.message));
      }
    });
    const uniqueMessages = uniq(messages);

    if (uniqueMessages.length) {
      return uniqueMessages.join(', ');
    }

    return 'Unknown error';
  }

  // if error is primitive then just string cast it
  return String(error || '');
};

export const mapCellTypeToInputType = ({
  contentType,
  content
}: TTableCellContent): InputWrapperProps['type'] => {
  switch (contentType) {
    case 'check': {
      return 'boolean';
    }
    case 'array': {
      return 'list';
    }
    case 'table': {
      return 'table';
    }
    case 'number': {
      return 'number';
    }
    case 'text': {
      if (typeof content === 'number') {
        return 'number';
      }

      return 'text';
    }
    case 'percent':
      return 'number'; // @TODO, maybe make a percentage input?
    case 'percent*100':
      return 'number'; // @TODO, maybe make a percentage input?
    case 'currency':
      return 'currency';
    case 'range':
      return 'range';
    case 'date':
      return 'date';
    case 'idAndDate':
    case 'hr':
    case 'address':
    default: {
      throw new Error(`Currently not implemented ${contentType} ${content}`);
    }
  }
};
